import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
   createDestination,
   deleteDestination,
   getDestinations,
   getDestinationsForTraveler,
   reorderDestinations,
   updateDestination,
} from 'src/api/destinationApi';
import { showToast } from 'src/components/toast';
import { queryFilter } from 'src/utils/proptypes';

export const DestinationQueryKeys = {
   GetDestinations: 'getDestinations',
   GetTravelerDestinations: 'getTravelerDestinations',
   CreatDestination: 'createDestination',
   UpdateDestination: 'updateDestination',
   DeleteDestination: 'deleteDestination',
   UpsertUserDestination: 'upsertUserDestination',
   ReorderDestinations: 'reorderDestinations',
};

export type MutationParams<TData = any, TError = any, TVariables = any> = {
   onSuccessCallBack?: (data?: TData) => void;
   onErrorCallBack?: (error?: TError) => void;
   onMutateCallBack?: (variables?: TVariables) => void;
   onSettledCallBack?: (data?: TData, error?: TError) => void;
};

export const useGetDestinationsByOrgId = (query: queryFilter, organizationId?: string) => {
   return useQuery({
      queryKey: [DestinationQueryKeys.GetDestinations, query, organizationId],

      queryFn: () => {
         if (!organizationId) throw new Error('OrganizationId is required');
         return getDestinations(query, organizationId);
      },
      retry: 0,
      enabled: !!organizationId,
   });
};

export const useGetDestinationsByOrgIdForTraveler = (query: queryFilter, organizationId?: string) => {
   return useQuery({
      queryKey: [DestinationQueryKeys.GetTravelerDestinations, query, organizationId],

      queryFn: () => {
         if (!organizationId) throw new Error('OrganizationId is required');
         return getDestinationsForTraveler(query, organizationId);
      },
      retry: 0,
      enabled: !!organizationId,
   });
};

export const useCreateDestination = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [DestinationQueryKeys.CreatDestination],
      mutationFn: createDestination,
      onError: (err: any) => {
         const { errors } = err.response?.data || [];

         if (errors && errors.length > 0) {
            showToast('error', errors[0]?.detail);
         } else {
            showToast('error', 'Error while adding new destination');
         }

         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [DestinationQueryKeys.GetDestinations] });
         showToast('success', 'Successfully added new destination');

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useUpdateDestination = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [DestinationQueryKeys.UpdateDestination],
      mutationFn: updateDestination,
      onError: () => {
         showToast('error', 'Error while updating destination');

         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [DestinationQueryKeys.GetDestinations] });

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useDeleteDestination = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [DestinationQueryKeys.DeleteDestination],
      mutationFn: deleteDestination,
      onError: () => {
         showToast('error', 'Error while deleting destination');

         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [DestinationQueryKeys.GetDestinations] });
         showToast('success', 'Successfully deleted destination');

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useReorderDestination = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [DestinationQueryKeys.ReorderDestinations],
      mutationFn: reorderDestinations,
      onError: () => {
         showToast('error', 'Error while reordering destinations');

         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [DestinationQueryKeys.GetTravelerDestinations] });

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};
